import React, { useState } from 'react';
import { ArrowLeft, Sparkles, User, Phone, Link as LinkIcon } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { setCustomerData, sendMetaEvent } from '../utils/metaCapi';

const countryCodes = [
  { code: '+20', label: 'مصر (+20)' },
  { code: '+966', label: 'السعودية (+966)' },
  { code: '+971', label: 'الإمارات (+971)' },
  { code: '+965', label: 'الكويت (+965)' },
  { code: '+973', label: 'البحرين (+973)' },
  { code: '+968', label: 'عمان (+968)' },
  { code: '+974', label: 'قطر (+974)' },
  { code: '+212', label: 'المغرب (+212)' },
  { code: '+213', label: 'الجزائر (+213)' },
  { code: '+216', label: 'تونس (+216)' },
  { code: '+964', label: 'العراق (+964)' },
  { code: '+962', label: 'الأردن (+962)' },
  { code: '+961', label: 'لبنان (+961)' },
  { code: '+1', label: 'أمريكا/كندا (+1)' },
  { code: '+44', label: 'بريطانيا (+44)' },
  { code: '', label: 'أخرى' },
];

export default function MediaBuying() {
  const [formData, setFormData] = useState({
    firstName: '',
    countryCode: '+20',
    phone: '',
    resumeLink: '',
    botField: '', // Honeypot
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validatePhone = (value) => {
    // Check if phone starts with country code patterns (e.g., +20, 20, 00, 0 followed by country code)
    const countryCodePatterns = /^\+?\d{1,4}/;
    if (countryCodePatterns.test(value) && value.length > 0) {
      // Check if it looks like a country code (starts with + or 00, or is a short number)
      if (value.startsWith('+') || value.startsWith('00') || (value.length <= 4 && /^\d+$/.test(value))) {
        return 'يرجى إدخال رقم الجوال فقط بدون مفتاح الدولة (مثال: 01234567891)';
      }
    }
    return '';
  };

  // Format phone number - remove any existing country code and add selected one
  const formatPhoneNumber = (phone, countryCode) => {
    // Remove +, 00, and country code digits from the beginning
    let cleaned = phone.replace(/^\+?00?/, '');
    
    // Remove common country code patterns (e.g., 20 for Egypt, 966 for Saudi)
    const countryCodeDigits = countryCode.replace(/\D/g, '');
    if (cleaned.startsWith(countryCodeDigits)) {
      cleaned = cleaned.substring(countryCodeDigits.length);
    }
    
    // Return with country code prefix
    return countryCode + cleaned;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'phone') {
      setPhoneError(validatePhone(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone before submission
    const phoneValidationError = validatePhone(formData.phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    
    // Honeypot check
    // If bots fill this, we immediately succeed visually but do nothing
    if (formData.botField) {
      setStatus('success');
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    setPhoneError('');

    const webhookUrl = import.meta.env.VITE_MEDIA_BUYING_WEBHOOK_URL || 'https://api.nodatx.com/webhook/media-buying';
    const authUser = import.meta.env.VITE_MEDIA_BUYING_AUTH_USER || '';
    const authPass = import.meta.env.VITE_MEDIA_BUYING_AUTH_PASS || '';

    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      if (authUser && authPass) {
        headers['Authorization'] = 'Basic ' + btoa(`${authUser}:${authPass}`);
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          firstName: formData.firstName,
          phone: formatPhoneNumber(formData.phone, formData.countryCode),
          resumeLink: formData.resumeLink,
          source: 'media_buying_application'
        })
      });

if (!response.ok) {
throw new Error('حدث خطأ أثناء إرسال طلبك. يرجى المحاولة مرة أخرى أو التواصل مع الدعم.');
}

// Store customer data for enhanced Meta CAPI match quality
await setCustomerData({
firstName: formData.firstName,
phone: formatPhoneNumber(formData.phone, formData.countryCode),
});

// Track successful application with Meta CAPI
sendMetaEvent('Lead', {
content_name: 'Media Buying Application',
content_category: 'Job Application',
status: 'completed',
source: 'media_buying_application',
value: 0,
currency: 'EGP',
});

// Also track with Meta Pixel for browser-side
if (typeof window !== 'undefined' && window.fbq) {
window.fbq('track', 'Lead', {
content_name: 'Media Buying Application',
content_category: 'Job Application',
status: 'completed'
});
}

setStatus('success');
      setFormData({
        firstName: '',
        countryCode: '+20',
        phone: '',
        resumeLink: '',
        botField: '' // Honeypot
      });
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'حدث خطأ غير متوقع. يرجى المحاولة لاحقاً.');
    }
  };

  return (
    <section className="relative min-h-[100dvh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 overflow-hidden font-sans" dir="rtl">
      <SEOHead
        title="التقديم لفرصة Media Buying — Nodatix"
        description="انضم لفريق Nodatix كمتخصص في Media Buying. أرسل طلبك الآن وكن جزءاً من مستقبل التسويق الرقمي."
        canonical="https://media-buying.nodatx.com"
      />
      {/* Premium Background Elements from Hero Section */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Deep background glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand-orange/20 via-brand-dark to-brand-dark"></div>
        {/* Animated Orbs */}
        <div className="absolute top-1/4 right-1/4 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-brand-orange/30 rounded-full mix-blend-screen filter blur-[100px] sm:blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-brand-blue/20 rounded-full mix-blend-screen filter blur-[120px] sm:blur-[150px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 w-[600px] sm:w-[800px] h-[600px] sm:h-[800px] bg-gradient-to-r from-brand-orange/10 to-brand-blue/10 rounded-full blur-[80px] sm:blur-[100px]"></div>
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:3rem_3rem] sm:bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      </div>

      <div className="relative z-10 w-full max-w-lg mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-md shadow-lg shadow-black/20 hover:bg-white/10 transition-colors duration-300 mb-6 mx-auto">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-orange opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-orange"></span>
            </span>
            <span className="text-white/90 font-medium text-sm tracking-wide">فرصة وظيفية</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-[1.1] text-white">
            انضم لفريق <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange via-[#ff8a3d] to-brand-blue animate-gradient-x">Nodatix</span>
          </h1>
          <p className="mt-4 sm:mt-6 text-base sm:text-lg text-gray-400 max-w-md mx-auto leading-relaxed font-light">
            نبحث عن متخصصين في Media Buying للانضمام لفريقنا. أرسل طلبك الآن!
          </p>
        </div>

        <div className="bg-[#0a0f1c]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-8 md:p-10 shadow-[0_0_80px_-20px_rgba(249,115,22,0.15)] relative overflow-hidden">
          {/* Visual grid in card */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:1.5rem_1.5rem]"></div>
          
          <div className="relative z-10 w-full min-h-[400px] flex flex-col justify-center">
            {status === 'success' ? (
              <div className="text-center py-8 animate-[scale-in_0.5s_cubic-bezier(0.34,1.56,0.64,1)]">
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-2xl bg-gradient-to-br from-green-400 to-green-500 shadow-[0_0_40px_rgba(74,222,128,0.4)] mb-8 drop-shadow-2xl relative group">
                  <div className="absolute inset-0 bg-green-400 rounded-2xl animate-ping opacity-20"></div>
                  <Sparkles className="h-10 w-10 text-white animate-pulse" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white mb-4 animate-[slide-up_0.6s_ease-out_0.1s_both]">تم إرسال طلبك بنجاح!</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-10 max-w-xs mx-auto animate-[slide-up_0.6s_ease-out_0.2s_both]">
                  لقد تم استلام طلبك، سنقوم بالتواصل معك قريباً لمراجعة بياناتك.
                </p>
                <a
                  href="https://nodatx.com"
                  className="animate-[slide-up_0.6s_ease-out_0.3s_both] w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 hover:scale-105 active:scale-95 transition-all font-medium text-sm sm:text-base cursor-pointer shadow-lg"
                >
                  العودة إلى Nodatix.cv
                </a>
              </div>
            ) : (
              <form className={`space-y-6 transition-opacity duration-300 ${status === 'loading' ? 'opacity-80 pointer-events-none' : 'opacity-100'}`} onSubmit={handleSubmit}>
                {/* Honeypot field - visually hidden, critical for spam reduction */}
                <div className="opacity-0 absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="botField">لاتملا هذا الحقل إذا كنت بشرياً</label>
                  <input
                    type="text"
                    name="botField"
                    id="botField"
                    value={formData.botField}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                      الاسم الكامل <span className="text-brand-orange">*</span>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500 group-hover:text-brand-orange transition-colors">
                        <User className="w-5 h-5" />
                      </div>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="appearance-none block w-full pr-12 pl-4 py-3 sm:py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all hover:bg-white/10 text-base"
                        placeholder="أدخل اسمك الكامل"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      رقم الجوال / الواتساب <span className="text-brand-orange">*</span>
                    </label>
                    <div className="flex flex-row gap-2 sm:gap-3">
                      <div className="relative w-[35%] sm:w-1/3 group">
                        <select
                          id="countryCode"
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleChange}
                          className="appearance-none block w-full px-3 py-3 sm:py-4 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all hover:bg-white/10 text-sm sm:text-base cursor-pointer text-right"
                          dir="rtl"
                        >
                          {countryCodes.map(c => (
                            <option key={c.code} value={c.code} className="bg-brand-dark text-white">{c.label}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-gray-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                      <div className="relative w-[65%] sm:w-2/3 group">
                         <div className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center pointer-events-none text-gray-500 group-hover:text-brand-orange transition-colors">
                          <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className={`appearance-none block w-full pr-10 sm:pr-12 pl-3 py-3 sm:py-4 bg-white/5 border rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 transition-all hover:bg-white/10 text-base text-left ${phoneError ? 'border-red-500 focus:ring-red-500' : 'border-white/10 focus:ring-brand-orange focus:border-transparent'}`}
                          placeholder="01234567891"
                          dir="ltr"
                        />
                      </div>
                    </div>
                    {phoneError && (
                      <p className="mt-2 text-xs text-red-400 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {phoneError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="resumeLink" className="block text-sm font-medium text-gray-300 mb-2">
                      رابط السيرة الذاتية / معرض الأعمال <span className="text-brand-orange">*</span>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500 group-hover:text-brand-blue transition-colors">
                        <LinkIcon className="w-5 h-5" />
                      </div>
                      <input
                        id="resumeLink"
                        name="resumeLink"
                        type="url"
                        required
                        value={formData.resumeLink}
                        onChange={handleChange}
                        className="appearance-none block w-full pr-12 pl-4 py-3 sm:py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all hover:bg-white/10 text-base text-left"
                        placeholder="https://drive.google.com/..."
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>

                {status === 'error' && (
                  <div className="rounded-xl bg-red-500/10 p-4 border border-red-500/20 flex items-start mt-4 animate-[slide-down_0.3s_ease-out]">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="mr-3">
                      <h3 className="text-sm font-medium text-red-300">{errorMessage}</h3>
                    </div>
                  </div>
                )}

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="group relative w-full flex items-center justify-center gap-3 px-8 py-4 sm:py-5 bg-brand-orange text-white font-bold rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] hover:shadow-[0_0_40px_-5px_rgba(249,115,22,0.6)] cursor-pointer disabled:opacity-90 disabled:hover:scale-100 disabled:cursor-not-allowed"
                  >
                    {/* Ripple/Glow effect on hover */}
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>
                    
                    {/* Ring animation on loading */}
                    {status === 'loading' && (
                      <div className="absolute inset-0 border-2 border-white/40 rounded-xl animate-ping opacity-20"></div>
                    )}
                    
                    {status === 'loading' ? (
                      <div className="flex items-center gap-3">
                        <svg className="animate-spin relative z-10 w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="relative z-10 text-base sm:text-lg animate-pulse">جاري الإرسال...</span>
                      </div>
                    ) : (
                      <>
                        <span className="relative z-10 text-base sm:text-lg">أرسل طلبك الآن</span>
                        <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 group-hover:-translate-x-1.5 transition-transform duration-300 relative z-10 drop-shadow-md" />
                      </>
                    )}
                  </button>
                  <p className="mt-5 text-center text-xs text-gray-500 flex justify-center items-center gap-1.5 font-medium transition-opacity duration-300">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                    بياناتك آمنة، ولا نشاركها مع طرف ثالث
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
